// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    alpha,
    CircularProgress
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    AdminPanelSettings
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { motion } from 'framer-motion';
import { authService } from '../apiServices/authService';

const LogIn = () => {
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        showPassword: false
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    // Check if already logged in as admin
    useEffect(() => {
        const user = authService.getCurrentUser();
        if (user && user.role === 'admin') {
            navigate('/dashboard');
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = () => {
        setFormData(prev => ({
            ...prev,
            showPassword: !prev.showPassword
        }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            email: '',
            password: ''
        };

        if (!formData.email) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleLogin = async () => {
        if (!validateForm()) {
            showToast("Warning", "Please fill in all required fields correctly", "warning");
            return;
        }

        setLoading(true);

        try {
            const result = await authService.login(formData.email, formData.password);

            if (result.success) {
                const userData = authService.getCurrentUser();
                showToast("Success", `Welcome ${userData?.name || 'Admin'}!`, "success");
                
                setTimeout(() => {
                    navigate('/dashboard');
                }, 500);
            } else {
                // Handle specific error messages from backend
                if (result.errors) {
                    // Show field-specific errors
                    if (result.errors.email) {
                        showToast("Error", result.errors.email[0], "error");
                    } else if (result.errors.password) {
                        showToast("Error", result.errors.password[0], "error");
                    } else {
                        showToast("Error", result.message || "Login failed", "error");
                    }
                } else {
                    showToast("Error", result.message || "Login failed", "error");
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            showToast("Error", "An unexpected error occurred. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleLogin();
        }
    };

    // Admin brand colors
    const adminColor = '#024e2a';
    const adminGradient = `linear-gradient(135deg, #024e2a 0%, #024e2a 50%, #024e2a 100%)`;

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 2 : 3,
            background: adminGradient,
        }}>
            {/* Decorative background elements */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
                zIndex: 1,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: alpha('#fff', 0.05),
                    transform: 'scale(2)',
                }
            }} />

            {/* Main Login Card */}
            <Fade in={true} timeout={800}>
                <Paper 
                    elevation={0}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: '100%',
                        maxWidth: isMobile ? '92%' : isTablet ? 420 : 480,
                        p: isMobile ? 3 : { xs: 4, sm: 5 },
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Logo and Title Section */}
                        <Zoom in={true} timeout={500}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 2,
                                    }}>
                                        <Box sx={{
                                            height: isMobile ? '70px' : '90px',
                                            width: isMobile ? '70px' : '90px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: adminGradient,
                                            borderRadius: '20px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        }}>
                                            <AdminPanelSettings sx={{ 
                                                fontSize: isMobile ? 40 : 50, 
                                                color: 'white' 
                                            }} />
                                        </Box>
                                    </Box>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <Typography
                                        variant={isMobile ? 'h5' : 'h4'}
                                        component="h1"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            color: adminColor,
                                        }}
                                    >
                                        Envsol Technologies
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: 'text.secondary',
                                            mt: 1,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Administrator Login
                                    </Typography>
                                </motion.div>
                            </Box>
                        </Zoom>

                        {/* Login Form */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                        >
                            <Box component="form" sx={{ mt: 2 }} onKeyDown={handleKeyPress}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    margin="normal"
                                    disabled={loading}
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: adminColor }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.common.white, 0.9),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.common.white, 1),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: adminColor,
                                            }
                                        }
                                    }}
                                    size={isMobile ? 'small' : 'medium'}
                                />

                                <TextField
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={formData.showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    margin="normal"
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: adminColor }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={togglePasswordVisibility}
                                                    edge="end"
                                                    disabled={loading}
                                                >
                                                    {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.common.white, 0.9),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.common.white, 1),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: adminColor,
                                            }
                                        }
                                    }}
                                    size={isMobile ? 'small' : 'medium'}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    disabled={loading}
                                    startIcon={!loading && <LoginIcon />}
                                    sx={{
                                        py: isMobile ? 1.2 : 1.5,
                                        mt: 3,
                                        mb: 2,
                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        background: adminGradient,
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 8px 25px ${alpha(adminColor, 0.4)}`
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Login as Administrator'
                                    )}
                                </Button>

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}
                                    >
                                        Envsol Technologies Uganda Limited
                                    </Typography>
                                </Box>
                            </Box>
                        </motion.div>
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
};

export default LogIn;